import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Heart, User, Search, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/deepavali-2026", label: "Deepavali 2026", festive: true },
  { to: "/shop?best=1", label: "Best Sellers" },
  { to: "/shop?new=1", label: "New Arrivals" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { itemCount, wishlist } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggests, setSuggests] = useState([]);
  const [openSug, setOpenSug] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showMega, setShowMega] = useState(false);
  const [categories, setCategories] = useState([]);
  const searchRef = useRef(null);
  const megaRef = useRef(null);

  useEffect(() => { api.get("/categories").then(({data}) => setCategories(data)); }, []);

  useEffect(() => {
    if (!q.trim()) { setSuggests([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/search-suggest", { params: { q } });
        setSuggests(data.items || []); setOpenSug(true);
      } catch {}
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (!searchRef.current?.contains(e.target)) setOpenSug(false);
      if (!megaRef.current?.contains(e.target)) setShowMega(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpenSug(false);
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-ivory border-b hairline" data-testid="site-header">
      <div className="bg-espresso text-ivory text-center text-xs md:text-sm py-2 tracking-wide">
        All India &amp; Worldwide Shipping Available
      </div>

      <div className="container-x flex items-center justify-between py-4 md:py-5 gap-4">
        {/* Brand */}
        <Logo size="md" />

        {/* Desktop search */}
        <div ref={searchRef} className="relative hidden md:flex flex-1 max-w-xl">
          <form onSubmit={submitSearch} className="w-full flex items-center border hairline rounded-full pl-4 pr-2 py-2 bg-white/60">
            <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search brass diya, ganesha, pooja items…"
              className="flex-1 bg-transparent px-3 py-1 outline-none text-sm" />
            <button type="submit" data-testid="search-submit" className="btn-gold rounded-full px-4 py-1 text-xs font-medium">Search</button>
          </form>
          {openSug && suggests.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border hairline rounded-xl shadow-sm overflow-hidden z-50">
              {suggests.map((s) => (
                <Link key={s.slug} to={`/product/${s.slug}`} onClick={() => setOpenSug(false)} className="flex items-center gap-3 p-2 hover:bg-muted" data-testid={`suggest-${s.slug}`}>
                  <img src={s.images?.[0]} className="w-10 h-10 object-cover rounded" alt="" />
                  <div className="flex-1 text-sm">{s.name}</div>
                  <div className="text-xs gold-text">₹{s.price}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Icons */}
        <div className="flex items-center gap-3 md:gap-4">
          <Link to="/account" data-testid="account-link" className="hidden md:flex items-center gap-1 text-sm hover:gold-text p-2.5 -m-2.5">
            <User className="w-5 h-5" strokeWidth={1.5} />
            <span className="hidden lg:inline">{user ? user.name?.split(" ")[0] : "Account"}</span>
          </Link>
          <Link to="/wishlist" data-testid="wishlist-link" className="relative hidden sm:block p-2.5 -m-2.5">
            <Heart className="w-5 h-5" strokeWidth={1.5} />
            {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-[#B8860B] text-ivory text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{wishlist.length}</span>}
          </Link>
          <Link to="/cart" data-testid="cart-link" className="relative p-2.5 -m-2.5">
            <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            {itemCount > 0 && <span className="absolute -top-2 -right-2 bg-[#B8860B] text-ivory text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{itemCount}</span>}
          </Link>
          <button data-testid="mobile-menu-btn" className="md:hidden p-2.5 -m-1.5" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Desktop nav with mega menu */}
      <nav className="hidden md:block border-t hairline relative" ref={megaRef}>
        <div className="container-x flex items-center gap-8 py-3 text-sm">
          <Link to="/" className="hover:gold-text" data-testid="nav-home">Home</Link>
          <Link to="/shop" className="hover:gold-text" data-testid="nav-shop">Shop</Link>
          <button
            onMouseEnter={() => setShowMega(true)}
            onClick={() => setShowMega(!showMega)}
            className={`flex items-center gap-1 hover:gold-text ${showMega ? "gold-text" : ""}`}
            data-testid="nav-categories"
          >
            Categories <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <Link to="/shop?best=1" className="hover:gold-text" data-testid="nav-best-sellers">Best Sellers</Link>
          <Link to="/shop?new=1" className="hover:gold-text" data-testid="nav-new-arrivals">New Arrivals</Link>
          <Link
            to="/deepavali-2026"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 px-3 py-1 gold-text hover:bg-[#B8860B] hover:text-ivory transition"
            data-testid="nav-deepavali"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B] animate-pulse" /> Deepavali 2026
          </Link>
          <Link to="/blog" className="hover:gold-text" data-testid="nav-blog">Blog</Link>
          <Link to="/faq" className="hover:gold-text" data-testid="nav-faq">FAQ</Link>
          <Link to="/about" className="hover:gold-text" data-testid="nav-about">About</Link>
          <Link to="/contact" className="hover:gold-text" data-testid="nav-contact">Contact</Link>
        </div>
        {showMega && (
          <div className="absolute top-full left-0 right-0 bg-ivory border-t border-b hairline shadow-sm" onMouseLeave={() => setShowMega(false)} data-testid="mega-menu">
            <div className="container-x py-8 grid grid-cols-3 gap-8">
              {categories.map((c) => (
                <div key={c.slug}>
                  <Link to={`/shop?category=${c.slug}`} onClick={() => setShowMega(false)} className="font-serif text-lg hover:gold-text block mb-2" data-testid={`mega-cat-${c.slug}`}>{c.name}</Link>
                  <div className="space-y-1">
                    {(c.subcategories || []).slice(0, 5).map((sc) => (
                      <Link key={sc} to={`/shop?category=${c.slug}&subcategory=${encodeURIComponent(sc)}`} onClick={() => setShowMega(false)} className="text-xs text-muted-foreground hover:gold-text block">{sc}</Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t hairline p-4 space-y-3 max-h-[80vh] overflow-y-auto">
          <form onSubmit={submitSearch} className="w-full flex items-center border hairline rounded-full pl-4 pr-2 py-2">
            <Search className="w-4 h-4" strokeWidth={1.5} />
            <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 bg-transparent px-3 py-1 outline-none text-sm" />
            <button type="submit" className="btn-gold rounded-full px-3 py-1 text-xs">Go</button>
          </form>
          <div className="grid gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="py-2 border-b hairline text-sm" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>{n.label}</Link>
            ))}
          </div>
          <div className="pt-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Categories</div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link key={c.slug} to={`/shop?category=${c.slug}`} onClick={() => setMobileOpen(false)} className="py-1.5 text-xs" data-testid={`mobile-cat-${c.slug}`}>{c.name}</Link>
              ))}
            </div>
          </div>
          <Link to="/account" onClick={() => setMobileOpen(false)} className="block py-2 text-sm border-t hairline mt-2">{user ? "My Account" : "Sign in / Register"}</Link>
        </div>
      )}
    </header>
  );
}
